/* eslint-disable camelcase */
import { useEffect, type FC, useState } from 'react';
import { Heading, Loader } from '@livechat/design-system-react-components';
import Cookies from 'js-cookie';

import Config from 'Config';
import { Cookie } from 'constants/cookies';
import { useNotifications } from 'contexts/notifications/use-notifications';
import { LocalStorageKey } from 'constants/local-storage';
import Location from 'services/Location';
import { detectProduct, getProductName } from 'helpers/product';
import LocalStorage from 'services/LocalStorage';
import ChatWidget from 'services/ChatWidget';
import { getItem } from 'helpers/storage/local-storage';
import { useSendVerificationCode } from 'hooks/use-send-verification-code';
import { useGetMe } from 'hooks/use-get-me';
import {
  isStateJwtToken,
  navigateToOriginalUrl,
  navigateToSelectedProduct,
} from 'helpers/navigation';
import { useOrganizations } from 'hooks/use-organizations';
import { useProducts } from 'hooks/use-products';
import { SelectTrial } from 'routes/select-trial/SelectTrial';

import { Layout } from 'components/layout/Layout';
import { FormMessage } from 'components/forms/FormMessage';
import { FakeLink } from 'components/fake-link/FakeLink';
import { VerificationCodeField } from 'components/verification-code-field/VerificationCodeField';
import { FormMessageType } from 'types/form';

import Amplitude from 'services/Amplitude';
import { OrganizationChooser } from '../chooser/OrganizationChooser';
import { JoinProductBlocked } from '../chooser/join-product/JoinProductBlocked';
import { OpenEmail } from './open-email/OpenEmail';
import { Description } from './description/Description';
import { SendAgain } from './send-again/SendAgain';
import { useGetEmailDomain } from './hooks/use-get-email-domain';

import * as styles from './styles';

export const StopScreen: FC = () => {
  const [organizationWithJoinBlocked, setOrganizationWithJoinBlocked] = useState<string>(null);
  const [shouldDisplayOrganizationsChooser, setShouldDisplayOrganizationsChooser] = useState(false);
  const [shouldDisplayTrialChooser, setShouldDisplayTrialChooser] = useState(false);
  const [isCheckingVerification, setIsCheckingVerification] = useState(false);
  const {
    email,
    accountId,
    isAccountVerified,
    isAccountWhitelisted,
    isAccountWhitelistedElsewhere,
    isFetching: isFetchingMe,
  } = useGetMe();
  const { domain, isFetching: isFetchingEmailDomain } = useGetEmailDomain(email);
  const {
    hasBeenTiggeredManually,
    hasEmailBounced,
    sendVerificationCode,
    isProcessing: isSendEmailProcessing,
  } = useSendVerificationCode(email);
  const { isFetching: isFetchingOrganizations, organizations } = useOrganizations();
  const { isFetchingProducts, products } = useProducts();
  const { showToast } = useNotifications();

  const shouldDisplayChooser = shouldDisplayTrialChooser || shouldDisplayOrganizationsChooser;
  const currentProductCookie = detectProduct();
  const hasBeenTiggeredAutomatically = Cookies.get(Cookie.VerificationCodeSend) === accountId;

  const handleShowChooser = (): void => {
    setIsCheckingVerification(true);

    if (isAccountWhitelisted) {
      setShouldDisplayOrganizationsChooser(true);
    } else {
      setShouldDisplayTrialChooser(true);
    }
  };

  const state = Location.getHashParam('state');
  const clientId = Location.getUrlParam('client_id') || getItem(LocalStorageKey.ClientId);

  useEffect(() => {
    if (!email) {
      return;
    }

    if (Location.getHashParam('utm_source') === LocalStorageKey.AccountsVerificationCodeReminder) {
      LocalStorage.set(LocalStorageKey.AccountsVerificationCodeReminder, true);
    }

    Amplitude.logEvent(
      email,
      'EMAIL_VERIFICATION_SCREEN_VISITED',
      {
        product: getProductName(currentProductCookie),
      },
      true
    );
  }, [email, currentProductCookie]);

  useEffect(() => {
    if (isFetchingOrganizations || isFetchingProducts || !!organizationWithJoinBlocked) {
      return;
    }

    if (shouldDisplayChooser) {
      if (
        organizations.filter((org) => !org.products?.length).length === 1 &&
        !products.some((product) => product.client_id === clientId)
      ) {
        navigateToSelectedProduct(email);
        return;
      }

      setIsCheckingVerification(false);
      return;
    }

    if (isAccountVerified) {
      setIsCheckingVerification(false);

      if (organizations?.length > 1) {
        setShouldDisplayOrganizationsChooser(true);
        return;
      }

      Cookies.remove(Cookie.VerificationCodeSend);
      showToast({
        content: 'Your email is confirmed',
        variant: 'info',
      });

      if (
        ((!clientId || clientId === Config.default_client_id) && currentProductCookie === 0) ||
        clientId === import.meta.env.VITE_APP_CLIENT_ID
      ) {
        handleShowChooser();
        setIsCheckingVerification(false);
        return;
      }

      navigateToOriginalUrl();
    }

    if (email && !hasBeenTiggeredAutomatically && !isAccountVerified && !isFetchingMe) {
      if (!isStateJwtToken(state)) {
        Cookies.remove('product');
      }

      sendVerificationCode();

      const expiresIn30Minutes = 1 / 48;
      Cookies.set(Cookie.VerificationCodeSend, accountId, { expires: expiresIn30Minutes });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    email,
    hasBeenTiggeredAutomatically,
    isAccountVerified,
    isFetchingMe,
    isFetchingOrganizations,
  ]);

  if (organizationWithJoinBlocked) {
    return <JoinProductBlocked organizationId={organizationWithJoinBlocked} />;
  }

  const isLoading =
    isFetchingMe || isFetchingEmailDomain || isFetchingProducts || isFetchingOrganizations;

  if (isLoading || isCheckingVerification || (isAccountVerified && !shouldDisplayChooser)) {
    return (
      <div css={styles.loaderContainer}>
        <Loader size="large" />
      </div>
    );
  }

  if (shouldDisplayTrialChooser) {
    return <SelectTrial isLoggedIn />;
  }

  if (shouldDisplayOrganizationsChooser) {
    return (
      <OrganizationChooser
        onSelectNewTrial={() => setShouldDisplayTrialChooser(true)}
        onJoinBlocked={setOrganizationWithJoinBlocked}
        isAccountWhitelisted={isAccountWhitelisted}
        isAccountWhitelistedElsewhere={isAccountWhitelistedElsewhere}
        showOnlyWhitelistedOrganizations
        isAccountVerified
      />
    );
  }

  return (
    <Layout backToLogin showHeaderLogo>
      <div css={styles.stopContainer}>
        {hasEmailBounced && (
          <div css={styles.errorContainer}>
            <FormMessage type={FormMessageType.Error} data-testid="stop-screen-email-bounced-error">
              We couldn’t deliver the email. Make sure the email address is correct. If you need
              more help,{' '}
              <FakeLink onClick={ChatWidget.handleContactSupport}>contact support</FakeLink>.
            </FormMessage>
          </div>
        )}
        <Heading size="lg">Enter confirmation code</Heading>
        <div css={styles.rows}>
          <Description email={email} />
          <VerificationCodeField
            email={email}
            isAccountWhitelisted={isAccountWhitelisted}
            onVerificationWithoutProductCompleted={handleShowChooser}
          />
          <OpenEmail domain={domain} />
          <SendAgain
            email={email}
            handleSendVerification={() => sendVerificationCode(true)}
            disabled={hasBeenTiggeredManually}
            loading={isSendEmailProcessing}
          />
        </div>
      </div>
    </Layout>
  );
};
